import React from 'react'

import {
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import clsx from 'clsx'

import { ScoreStatuses } from '$extensionSrc/components/ImpactMetrics/types'
import { MetricsCategories } from '$serverSrc/components/ImpactMetricsReport/types'

import EnterpriseReportBox from './EnterpriseReportBox'
import styles from './ProtectionScoresSection.module.scss'

interface ProtectionScoresSectionProps {
  loading: boolean
  numUsersPerBucket?: Array<{ bucket: string; numUsers: number } | null>
  numUsersPerBucketPerConcernArea?: Array<{
    bucket: string
    numUsers: number
    concernArea: string
  } | null>
}

// Helper component for protection scores with indicators
function ProtectionScoresHeader({
  numUsersPerBucket,
}: {
  numUsersPerBucket?: Array<{ bucket: string; numUsers: number } | null>
}) {
  return (
    <div className={styles.protectionScoresHeader}>
      <h2 className={styles.protectionScoresTitle}>Protection scores</h2>
      <div className={styles.protectionScoresIndicators}>
        {ScoreStatuses.map((bucket) => (
          <div key={bucket} className={styles.protectionScoreIndicator}>
            <div
              className={`${styles.protectionScoreDot} ${styles[bucket.toLowerCase()]}`}
            />
            {numUsersPerBucket?.find((b) => b?.bucket === bucket)?.numUsers ||
              0}
          </div>
        ))}
      </div>
    </div>
  )
}

export default function ProtectionScoresSection({
  loading,
  numUsersPerBucket,
  numUsersPerBucketPerConcernArea,
}: ProtectionScoresSectionProps) {
  return (
    <EnterpriseReportBox
      header={<ProtectionScoresHeader numUsersPerBucket={numUsersPerBucket} />}
      content={
        <Table className={styles.protectionScoresTable}>
          <TableHead>
            <TableRow>
              <TableCell className="body2">Category</TableCell>
              <TableCell align="center">
                <Box className={styles.protectionColumnHeader}>
                  <Box
                    className={clsx(styles.protectionScoreDot, styles.high)}
                  />
                  High
                </Box>
              </TableCell>
              <TableCell align="center">
                <Box className={styles.protectionColumnHeader}>
                  <Box
                    className={clsx(styles.protectionScoreDot, styles.moderate)}
                  />
                  Moderate
                </Box>
              </TableCell>
              <TableCell align="center">
                <Box className={styles.protectionColumnHeader}>
                  <Box
                    className={clsx(styles.protectionScoreDot, styles.minimal)}
                  />
                  Minimal
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {MetricsCategories.map((concernArea) => {
              const buckets =
                numUsersPerBucketPerConcernArea?.filter(
                  (b) => b?.concernArea === concernArea,
                ) || []
              return (
                <TableRow key={concernArea}>
                  <TableCell>{concernArea}</TableCell>
                  <TableCell align="center">
                    {loading ? (
                      <Skeleton width={20} />
                    ) : (
                      <Typography variant="body2">
                        {buckets.find((b) => b?.bucket === 'High')?.numUsers ||
                          0}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {loading ? (
                      <Skeleton width={20} />
                    ) : (
                      <Typography variant="body2">
                        {buckets.find((b) => b?.bucket === 'Moderate')
                          ?.numUsers || 0}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {loading ? (
                      <Skeleton width={20} />
                    ) : (
                      <Typography variant="body2">
                        {buckets.find((b) => b?.bucket === 'Minimal')
                          ?.numUsers || 0}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      }
    />
  )
}
